import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from './Layout';
import StatusInfo from '../components/StatusInfo';
import ListPager from '../components/ListPager';
import { referralLabel } from '../utils/referralStatus';
import { fmtMoney } from '../utils/format';

const API = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000';
const TX_LIMIT = 20;

export default function PatientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState('referrals');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [committed, setCommitted] = useState({ from: '', to: '' });
  const [error, setError] = useState('');

  const key = localStorage.getItem('adminKey');
  const headers = { 'X-Admin-Key': key };

  const load = useCallback((from, to, off = 0) => {
    const params = { ref_offset: off };
    if (from && to) { params.date_from = from; params.date_to = to; }
    axios.get(`${API}/admin/patients/${id}`, { headers, params })
      .then(r => { setData(r.data); setError(''); })
      .catch(() => setError('Не удалось загрузить пациента'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const apply = () => { setCommitted({ from: dateFrom, to: dateTo }); setTxOffset(0); load(dateFrom, dateTo, 0); };
  const reset = () => { setDateFrom(''); setDateTo(''); setCommitted({ from: '', to: '' }); setTxOffset(0); load('', '', 0); };
  const goPage = (off) => load(dateFrom, dateTo, off);

  // N-110: вкладка «Транзакции» — все оплаты пациента по всем его направлениям, без
  // отдельного похода на общую страницу «Транзакции» и поиска там по ФИО.
  const [txItems, setTxItems] = useState([]);
  const [txTotal, setTxTotal] = useState(0);
  const [txOffset, setTxOffset] = useState(0);
  const [txSort, setTxSort] = useState('paid_date');
  const [txOrder, setTxOrder] = useState('desc');

  const fetchTx = useCallback(() => {
    const params = { patient_id: id, sort: txSort, order: txOrder, limit: TX_LIMIT, offset: txOffset };
    if (committed.from && committed.to) { params.date_from = committed.from; params.date_to = committed.to; }
    axios.get(`${API}/admin/transactions`, { headers, params })
      .then(r => { setTxItems(r.data.items); setTxTotal(r.data.total); }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, txSort, txOrder, txOffset, committed]);

  useEffect(() => { if (tab !== 'transactions') return; const t = setTimeout(fetchTx, 250); return () => clearTimeout(t); }, [tab, fetchTx]);

  const txToggleSort = (f) => { setTxOffset(0); if (txSort === f) setTxOrder(o => o === 'asc' ? 'desc' : 'asc'); else { setTxSort(f); setTxOrder('asc'); } };
  const txArrow = (f) => (txSort !== f ? '' : txOrder === 'asc' ? ' ▲' : ' ▼');

  if (error) return <Layout><div style={styles.empty}>{error}</div></Layout>;
  if (!data) return <Layout><div style={styles.empty}>Загрузка…</div></Layout>;

  const TabBtn = ({ id: tid, label }) => (
    <div style={{ ...styles.tab, ...(tab === tid ? styles.tabActive : {}) }} onClick={() => setTab(tid)}>{label}</div>
  );

  return (
    <Layout>
      <div style={styles.back} onClick={() => navigate('/patients')}>← К списку пациентов</div>

      <div style={styles.header}>
        <h1 style={styles.title}>{data.name}</h1>
      </div>

      <div style={styles.card}>
        <div style={styles.grid}>
          <Field label="Дата рождения" value={data.birth_date ? new Date(data.birth_date).toLocaleDateString('ru') : '—'} />
          <Field label="Добавлен" value={new Date(data.created_at).toLocaleDateString('ru')} />
          <Field label="ID" value={data.id} />
        </div>
      </div>

      <div style={styles.filterBar}>
        <button style={styles.btnGhost} onClick={reset}>Всё время</button>
        <input type="date" style={styles.dateInput} value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
        <span style={{ color: '#64748b' }}>—</span>
        <input type="date" style={styles.dateInput} value={dateTo} onChange={e => setDateTo(e.target.value)} />
        <button style={styles.btn} onClick={apply} disabled={!dateFrom || !dateTo}>Применить</button>
      </div>

      <div style={styles.metrics}>
        <Metric label="Направлений" value={data.referrals_count} />
        <Metric label="Оплачено в кассу" value={`${fmtMoney(data.total_paid)} ₽`} color="#2EC4B6" />
      </div>
      {data.has_period && (
        <div style={styles.note}>Направления и оплаты — точные по дате. Период фильтрует направления по дате привязки, транзакции — по дате оплаты.</div>
      )}

      <div style={styles.tabs}>
        <TabBtn id="referrals" label="Направления" />
        <TabBtn id="transactions" label="Транзакции" />
      </div>

      {tab === 'referrals' && (
        <>
          <div style={styles.table}>
            <div style={{ ...styles.thead, gridTemplateColumns: cols }}>
              <div style={styles.th}>ID</div>
              <div style={styles.th}>Врач</div>
              <div style={styles.th}>ID врача</div>
              <div style={styles.th}>Клиника</div>
              <div style={styles.th}>Статус<StatusInfo def="referral" /></div>
              <div style={styles.th}>Дата $ заявлено<StatusInfo def="duplicate_first_payment" /></div>
              <div style={styles.th}>Привязан</div>
              <div style={styles.th}>Первичный визит</div>
              <div style={styles.th}>Оплат</div>
              <div style={styles.th}>Сумма оплат</div>
            </div>
            {data.referrals.map(r => (
              <div key={r.id} style={{ ...styles.row, gridTemplateColumns: cols }}>
                <div style={{ ...styles.td, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', color: '#64748b', fontSize: 12 }}>REF-{r.id}</div>
                <div style={{ ...styles.td, ...styles.link }} onClick={() => navigate(`/doctors/${r.doctor_id}`)}>{r.doctor_name}</div>
                <div style={{ ...styles.td, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', color: '#64748b', fontSize: 12 }}>{r.doctor_public_id || '—'}</div>
                <div style={{ ...styles.td, ...styles.link }} onClick={() => navigate(r.target_type === 'network' ? `/networks/${r.clinic_id}` : `/clinics/${r.clinic_id}`)}>{r.clinic_name}</div>
                <div style={styles.td}>{referralLabel(r.status, r.clinic_confirmed_at)}</div>
                <div style={{ ...styles.td, color: '#64748b', fontSize: 12 }}>{r.status === 'duplicate' && r.duplicate_first_payment_date ? new Date(r.duplicate_first_payment_date).toLocaleDateString('ru') : '—'}</div>
                <div style={{ ...styles.td, color: '#64748b', fontSize: 12 }}>{new Date(r.bound_at).toLocaleDateString('ru')}</div>
                <div style={{ ...styles.td, color: '#64748b', fontSize: 12 }}>{r.primary_visit_at ? new Date(r.primary_visit_at).toLocaleDateString('ru') : '—'}</div>
                <div style={styles.td}>{r.transactions_count}</div>
                <div style={styles.td}>{fmtMoney(r.transactions_total)} ₽</div>
              </div>
            ))}
            {data.referrals.length === 0 && <div style={styles.empty}>Нет направлений за период</div>}
          </div>
          <Pager data={data} onPage={goPage} />
        </>
      )}

      {tab === 'transactions' && (
        <>
          <div style={styles.table}>
            <div style={{ ...styles.thead, gridTemplateColumns: txCols }}>
              <div style={{ ...styles.th, ...styles.thClickable }} onClick={() => txToggleSort('paid_date')}>Дата оплаты{txArrow('paid_date')}</div>
              <div style={{ ...styles.th, ...styles.thClickable }} onClick={() => txToggleSort('amount')}>Сумма{txArrow('amount')}</div>
              <div style={{ ...styles.th, ...styles.thClickable }} onClick={() => txToggleSort('is_primary')}>Тип{txArrow('is_primary')}</div>
              <div style={{ ...styles.th, ...styles.thClickable }} onClick={() => txToggleSort('doctor_name')}>Врач{txArrow('doctor_name')}</div>
              <div style={styles.th}>ID врача</div>
              <div style={{ ...styles.th, ...styles.thClickable }} onClick={() => txToggleSort('clinic_name')}>Клиника / сеть{txArrow('clinic_name')}</div>
              <div style={styles.th}>Направление</div>
            </div>
            {txItems.map(t => (
              <div key={t.id} style={{ ...styles.row, gridTemplateColumns: txCols }}>
                <div style={styles.td}>{new Date(t.paid_date).toLocaleDateString('ru')}</div>
                <div style={{ ...styles.td, color: '#2EC4B6', fontWeight: 600 }}>{fmtMoney(t.amount)} ₽</div>
                <div style={styles.td}>
                  {t.is_primary
                    ? <span style={styles.badgeTeal}>Первичная</span>
                    : <span style={styles.badgeGray}>Повторная</span>}
                </div>
                <div style={{ ...styles.td, ...styles.link }} onClick={() => navigate(`/doctors/${t.doctor_id}`)}>{t.doctor_name}</div>
                <div style={{ ...styles.td, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', color: '#64748b', fontSize: 12 }}>{t.doctor_public_id || '—'}</div>
                <div style={{ ...styles.td, ...styles.link }} onClick={() => navigate(t.target_type === 'network' ? `/networks/${t.target_id}` : `/clinics/${t.target_id || t.clinic_id}`)}>{t.clinic_name}</div>
                <div style={{ ...styles.td, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', color: '#64748b', fontSize: 12 }}>REF-{t.referral_id}</div>
              </div>
            ))}
            {txItems.length === 0 && <div style={styles.empty}>Транзакций не найдено</div>}
          </div>
          <ListPager total={txTotal} limit={TX_LIMIT} offset={txOffset} onPage={setTxOffset} />
        </>
      )}
    </Layout>
  );
}

function Pager({ data, onPage }) {
  const total = data.referrals_total ?? 0;
  const limit = data.ref_limit ?? 20;
  const offset = data.ref_offset ?? 0;
  if (total <= limit) return null;
  return (
    <div style={styles.pager}>
      <button style={styles.btnGhost} disabled={offset === 0} onClick={() => onPage(Math.max(0, offset - limit))}>← Назад</button>
      <span style={styles.pagerInfo}>{offset + 1}–{Math.min(offset + limit, total)} из {total}</span>
      <button style={styles.btnGhost} disabled={offset + limit >= total} onClick={() => onPage(offset + limit)}>Вперёд →</button>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div style={styles.fieldLabel}>{label}</div>
      <div style={styles.fieldValue}>{value}</div>
    </div>
  );
}

function Metric({ label, value, color }) {
  return (
    <div style={styles.metric}>
      <div style={styles.metricLabel}>{label}</div>
      <div style={{ ...styles.metricValue, ...(color ? { color } : {}) }}>{value}</div>
    </div>
  );
}

const cols = '0.8fr 1.6fr 1fr 1.6fr 1.2fr 1.1fr 1fr 1fr 0.7fr 1fr';
const txCols = '1fr 1fr 1fr 1.6fr 1fr 1.6fr 0.8fr';

const styles = {
  back: { color: '#64748b', fontSize: 13, cursor: 'pointer', marginBottom: 16 },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 },
  title: { color: 'white', fontSize: 24, fontWeight: 700, margin: 0 },
  card: { background: '#1e293b', borderRadius: 12, padding: 20, marginBottom: 24 },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 20 },
  fieldLabel: { fontSize: 12, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 },
  fieldValue: { fontSize: 14, color: '#e2e8f0' },
  pager: { display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 12, marginTop: 12 },
  pagerInfo: { color: '#64748b', fontSize: 13 },
  filterBar: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20 },
  dateInput: { background: '#1e293b', color: '#e2e8f0', border: '1px solid #334155', borderRadius: 6, padding: '6px 10px', fontSize: 13, outline: 'none' },
  btn: { background: '#2EC4B6', color: '#0f172a', border: 'none', borderRadius: 6, padding: '7px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  btnGhost: { background: 'transparent', color: '#94a3b8', border: '1px solid #334155', borderRadius: 6, padding: '7px 16px', fontSize: 13, cursor: 'pointer' },
  metrics: { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 16, marginBottom: 8 },
  metric: { background: '#1e293b', borderRadius: 12, padding: 20 },
  metricLabel: { fontSize: 13, color: '#64748b', marginBottom: 8 },
  metricValue: { fontSize: 26, fontWeight: 700, color: '#e2e8f0' },
  note: { color: '#64748b', fontSize: 12, marginBottom: 24 },
  tabs: { display: 'flex', gap: 4, borderBottom: '1px solid #334155', marginBottom: 20 },
  tab: { padding: '10px 18px', color: '#94a3b8', fontSize: 14, fontWeight: 600, cursor: 'pointer', borderBottom: '2px solid transparent', marginBottom: -1 },
  tabActive: { color: '#2EC4B6', borderBottom: '2px solid #2EC4B6' },
  table: { background: '#1e293b', borderRadius: 12, overflow: 'hidden' },
  thead: { display: 'grid', padding: '12px 20px', borderBottom: '1px solid #334155' },
  th: { fontSize: 12, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 },
  thClickable: { cursor: 'pointer', userSelect: 'none' },
  row: { display: 'grid', padding: '14px 20px', borderBottom: '1px solid #1e293b', background: '#0f172a', margin: '1px 0' },
  td: { fontSize: 14, color: '#e2e8f0', display: 'flex', alignItems: 'center' },
  link: { color: '#2EC4B6', cursor: 'pointer' },
  badgeTeal: { background: '#042f2e', color: '#2EC4B6', padding: '3px 8px', borderRadius: 20, fontSize: 12 },
  badgeGray: { background: '#1e293b', color: '#64748b', padding: '3px 8px', borderRadius: 20, fontSize: 12, border: '1px solid #334155' },
  empty: { color: '#64748b', fontSize: 14, textAlign: 'center', padding: '32px 0' },
};
